import styled from 'styled-components';
import { CommonRouterLink } from 'components/common';
import theme from 'theme';
import { Icon, Popconfirm, Tag } from 'antd';
import * as React from 'react';
import { ReactNode } from 'react';
import t from 'utils/translations';
import {
    formatBusinessId,
    formatMoney,
    formatStatementDate
} from 'utils/formatting';
import PerformerStars from 'components/common/PerformerStars';
import RiskRatingBalls from 'components/common/RiskRatingBalls';
import GroupActionMenu, {
    GroupActionMenuProps
} from 'components/pages/grouplistpage/actionmenu/GroupActionMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    BasicKeyFigureInfo,
    GroupsByCategories,
    RiskRatingClass
} from 'components/context/types';
import { ColumnProps } from 'antd/es/table';
import { CompanyTableSortField } from 'components/CommonGroupAndCategoryStuff/CompanyTable';

export interface NameInfo {
    name: string;
    link?: string;
    businessId?: string;
    onClickFunction?: () => void;
    tags?: ReactNode[];
}

export interface GroupTableInfo {
    groupId: string;
    groupName: string;
    numberOfCompanies: number;
    companies?: BasicKeyFigureInfo[];
}

export interface GroupTableActions {
    actionMenuProps?: GroupActionMenuProps;
    deleteAction?: () => void;
}

export enum Field {
    EMPTY = ''
}

export const RouterLinkTable = styled(CommonRouterLink)`
    display: inline-block;
    font-family: ${theme.fonts.openSans_bold};
`;

export const NonLinkName = styled.span`
    color: ${theme.colors.main};
    cursor: pointer;
    font-size: ${theme.typography.base_font_size};
    font-family: ${theme.fonts.openSans_bold};
`;

export const ActionButtonTable = styled.button`
    background-color: transparent;
`;
export const BusinessId = styled.p`
    color: ${theme.colors.gray_dark};
`;
export const FlexName = styled.div`
    display: inline-flex;
    flex-wrap: wrap;
`;

const ConcernTag = styled(Tag)`
    && {
        margin-left: 6px;
        height: 22px;
    }
`;

export const customExpandIcon = (props: any) => {
    if (props.record.children === undefined) {
        return <></>;
    }

    if (!props.expanded) {
        return (
            <span
                style={{ color: 'black', paddingRight: '6px' }}
                onClick={e => {
                    props.onExpand(props.record, e);
                }}
            >
                <Icon type="right-circle" theme="filled" />
            </span>
        );
    } else if (props.expanded) {
        return (
            <span
                style={{ color: 'black', paddingRight: '6px' }}
                onClick={e => {
                    props.onExpand(props.record, e);
                }}
            >
                <Icon type="down-circle" />
            </span>
        );
    }
    return <></>;
};

export const GroupCompanyTableColumns: Array<ColumnProps<any>> = [
    {
        title: t('NAME'),
        dataIndex: CompanyTableSortField.Name,
        key: CompanyTableSortField.Name,
        sorter: () => 0,
        render: ({
            name,
            link,
            onClickFunction,
            tags,
            businessId
        }: NameInfo) => (
            <FlexName>
                {link ? (
                    <RouterLinkTable to={link}>
                        <>
                            {name}
                            <BusinessId>
                                {formatBusinessId(businessId)}
                            </BusinessId>
                        </>
                    </RouterLinkTable>
                ) : (
                    <NonLinkName
                        onClick={onClickFunction ? onClickFunction : undefined}
                    >
                        <>
                            {name}
                            <BusinessId>
                                {formatBusinessId(businessId)}
                            </BusinessId>
                        </>
                    </NonLinkName>
                )}
                {tags && tags.map((tag: ReactNode) => tag)}
            </FlexName>
        )
    },
    {
        title: t('COMPANY_STATUS'),
        dataIndex: CompanyTableSortField.Status,
        key: CompanyTableSortField.Status,
        sorter: () => 0,
        render: (companyStatus: string) => {
            if (companyStatus === Field.EMPTY) {
                return Field.EMPTY;
            }
            return companyStatus ? `${companyStatus}` : t('NOT_AVAILABLE');
        }
    },
    {
        title: t('LATEST_STATEMENT'),
        dataIndex: CompanyTableSortField.StatementDate,
        key: CompanyTableSortField.StatementDate,
        sorter: () => 0,
        render: (statementDate?: string) => {
            if (statementDate === Field.EMPTY) {
                return Field.EMPTY;
            }
            return formatStatementDate(statementDate);
        }
    },
    {
        title: t('TURNOVER'),
        dataIndex: CompanyTableSortField.Turnover,
        key: CompanyTableSortField.Turnover,
        align: 'right' as 'left' | 'right' | 'center',
        sorter: () => 0,
        render: (turnover?: number | string) =>
            turnover !== Field.EMPTY
                ? formatMoney(turnover as number)
                : Field.EMPTY
    },
    {
        title: t('NET_PROFIT'),
        dataIndex: CompanyTableSortField.NetProfit,
        key: CompanyTableSortField.NetProfit,
        align: 'right' as 'left' | 'right' | 'center',
        sorter: () => 0,
        render: (netProfit?: number | string) =>
            netProfit !== Field.EMPTY
                ? formatMoney(netProfit as number)
                : Field.EMPTY
    },
    {
        title: t('PERFORMER_RANKING'),
        dataIndex: CompanyTableSortField.PerformerPoints,
        key: CompanyTableSortField.PerformerPoints,
        sorter: () => 0,
        render: (performerRankingPoints?: number | string) => {
            if (performerRankingPoints === Field.EMPTY) {
                return Field.EMPTY;
            }
            return performerRankingPoints !== undefined ? (
                <PerformerStars
                    performerRankingPoints={performerRankingPoints as number}
                    fontSize={10}
                />
            ) : (
                <span>{t('NOT_AVAILABLE')}</span>
            );
        }
    },
    {
        title: t('RISK_CLASS'),
        dataIndex: CompanyTableSortField.RiskRating,
        key: CompanyTableSortField.RiskRating,
        sorter: () => 0,
        render: (riskRatingClass?: RiskRatingClass | Field.EMPTY) => {
            if (riskRatingClass === Field.EMPTY) {
                return Field.EMPTY;
            }
            return riskRatingClass ? (
                <RiskRatingBalls
                    riskRatingClass={riskRatingClass}
                    height={20}
                />
            ) : (
                <span>{t('NOT_AVAILABLE')}</span>
            );
        }
    },
    {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        render: (actions: GroupTableActions) => {
            if (!actions) {
                return <></>;
            }
            if (actions.actionMenuProps) {
                return <GroupActionMenu {...actions.actionMenuProps} />;
            } else if (actions.deleteAction) {
                return (
                    <Popconfirm
                        onConfirm={actions.deleteAction}
                        title={t('CONFIRM_DELETE_BUSINESS')}
                        placement={'left'}
                    >
                        <ActionButtonTable>
                            <FontAwesomeIcon icon={['fas', 'trash']} />
                        </ActionButtonTable>
                    </Popconfirm>
                );
            }
            return <></>;
        }
    }
];

export function buildCategoryTabDataSource(
    groups: GroupTableInfo[],
    groupsByCategories: GroupsByCategories[],
    afterGroupActionsFunction: () => void,
    deleteCompanyFromGroup: (groupId: string, businessId: string) => void,
    changeTab?: (newTab: string) => void
) {
    return groups.map(group => {
        return {
            key: group.groupId,
            companyName: {
                name: group.groupName,
                onClickFunction: () => changeTab && changeTab(group.groupId)
            } as NameInfo,
            businessId: Field.EMPTY,
            companyStatus: Field.EMPTY,
            statementDate: Field.EMPTY,
            turnover: Field.EMPTY,
            netProfit: Field.EMPTY,
            performerRankingPoints: Field.EMPTY,
            riskRatingClass: Field.EMPTY,
            actions: {
                actionMenuProps: {
                    afterActionsFunction: afterGroupActionsFunction,
                    groupsByCategories,
                    id: group.groupId,
                    name: group.groupName,
                    numberOfCompaniesInGroup: group.numberOfCompanies,
                    activatingElement: <Icon type="setting" theme="filled" />
                } as GroupActionMenuProps
            } as GroupTableActions,
            children:
                group.companies && group.companies.length > 0
                    ? buildCompanyDataSource(
                          group.companies,
                          (businessId: string) =>
                              deleteCompanyFromGroup(group.groupId, businessId)
                      )
                    : []
        };
    });
}

export function buildCompanyDataSource(
    companies: BasicKeyFigureInfo[],
    deleteCompany: (businessId: string) => void
) {
    return companies.map(company => ({
        key: `${company.businessId}-${company.concern ? 'concern' : 'company'}`,
        companyName: {
            name: company.companyName,
            businessId: company.businessId,
            link: `/yritys/${company.businessId}`,
            tags: company.concern
                ? [
                      <ConcernTag key={company.businessId}>
                          {t('CONCERN')}
                      </ConcernTag>
                  ]
                : undefined
        } as NameInfo,
        companyStatus: company.companyStatus,
        statementDate: company.statementDate,
        turnover: company.turnover,
        netProfit: company.netProfit,
        performerRankingPoints: company.performerRankingPoints,
        riskRatingClass: company.riskRatingClass,
        actions: {
            deleteAction: () => deleteCompany(company.businessId)
        } as GroupTableActions
    }));
}
